import React, { useState, useEffect, useMemo } from 'react';
import api from '../../../../services/api';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  TableSortLabel,
  Pagination,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider,
  Skeleton,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  ArrowUpward as ArrowUpwardIcon,
  Domain as DomainIcon,
  ArrowDownward as ArrowDownwardIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { getDomains, createDomain, updateDomain, deleteDomain } from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import ConfirmDialog from '../../../molecules/ConfirmDialog/ConfirmDialog';


const DomainsManagementPanel = () => {
  const theme = useMuiTheme();
  const [domains, setDomains] = useState([]);
  const [totalDomains, setTotalDomains] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStrategicCategories, setFilterStrategicCategories] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainHistory, setDomainHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Helper de formatação de números (PT-BR)
  const formatNumber = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value);
  };

  // Helper de parser (1.000,00 -> 1000.00)
  const parseNumber = (value, isFloat = false) => {
    if (!value) return 0;
    // Remove pontos de milhar e troca vírgula por ponto
    const clean = value.toString().replace(/\./g, '').replace(',', '.');
    return isFloat ? parseFloat(clean) : parseInt(clean);
  };

  // Lógica de cálculo de bônus para sites pequenos
  // Sincronizada com backend para consistência
  const calcularBonusPequenos = (visitantesDia) => {
    if (!visitantesDia || visitantesDia <= 0) return 1.0;
    if (visitantesDia >= 10000) return 1.0;

    // Bônus Suave: 5 - log10(visitantes)
    // 10 visitas -> 5 - 1 = 4x
    // 100 visitas -> 5 - 2 = 3x
    // 1.000 visitas -> 5 - 3 = 2x
    // 10.000 visitas -> 5 - 4 = 1x
    const logVisitantes = Math.log10(visitantesDia);
    let bonus = 5 - logVisitantes;

    return Math.max(1.0, Math.min(4.0, bonus));
  };

  // Lógica de cálculo de CPC - Fórmula logarítmica (sincronizada com backend)
  // Recebe audiência DIÁRIA e retorna CPC
  const calculateCPC = (visitantesDia) => {
    if (!visitantesDia || visitantesDia <= 0) return 0;

    let cpcBase;

    // Para mega-portais (>1M visitas/dia), aplica compressão logarítmica adicional
    if (visitantesDia > 1000000) {
      const BASE = 154.3;
      const ESCALA = 2.968;
      const cpcBase1M = BASE / Math.pow(Math.log10(1000010), ESCALA);
      const val1M = cpcBase1M * 1000000;

      const fatorMultiplicador = visitantesDia / 1000000;
      const compressao = 1 + (Math.log10(fatorMultiplicador) * 0.5);

      const valoracao = val1M * compressao;
      cpcBase = valoracao / visitantesDia;
    } else {
      // Para sites menores (<1M), usa fórmula logarítmica normal
      const BASE = 154.3;
      const ESCALA = 2.968;

      cpcBase = BASE / Math.pow(Math.log10(visitantesDia + 10), ESCALA);
    }

    // Aplicar bônus para sites pequenos
    const bonus = calcularBonusPequenos(visitantesDia);
    const cpcComBonus = cpcBase * bonus;

    // Limites de CPC
    return Math.max(0.01, Math.min(400, cpcComBonus));
  };

  // Função para recalcular valores derivados
  const recalculateValues = (monthly, ppvVal) => {
    const monthlyNum = parseInt(monthly) || 0;
    const ppvNum = parseFloat(ppvVal) || 0;

    if (monthlyNum > 0) {
      const daily = Math.round(monthlyNum / (365 / 12));
      const pvMonthly = Math.round(monthlyNum * ppvNum);
      const pvDaily = Math.round(daily * ppvNum);

      // CPC é calculado com base na audiência DIÁRIA
      const cpc = calculateCPC(daily);
      // Valoração = CPC × audiência DIÁRIA
      const valuation = Math.round(cpc * daily);

      let tier = 3;
      if (daily >= 100000) tier = 1;
      else if (daily >= 10000) tier = 2;

      return {
        audience: daily, // Daily
        audienceDaily: daily,
        audienceMonthly: monthlyNum,
        pageViews: pvDaily,
        pageViewsMonthly: pvMonthly,
        valuation: valuation,
        cpc: cpc, // Incluir CPC para exibição
        tier: tier
      };
    }
    return {};
  };

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);

  // Estados para ordenação
  const [sortField, setSortField] = useState('siteName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [domainData, setDomainData] = useState({
    domain: '',
    siteName: '',
    category: 'site',
    categories: [],
    estado: '',
    uf: '',
    tipo: 'Site',
    audience: 0,
    audienceMonthly: 0,
    audienceDaily: 0,
    subscribers: 0,
    pageViews: 0,
    pageViewsMonthly: 0,
    ppv: 2.5,
    valuation: 0,
    tier: 3,
    description: '',
    updateSource: 'automatic'
  });

  // Estado separado para texto do PPV (permite digitação com vírgula)
  const [ppvText, setPpvText] = useState('2,5');

  const categories = ['site', 'portal', 'blog', 'rede-social', 'governo'];
  const tipos = ['Site', 'Portal', 'Blog', 'Rede Social', 'Governo'];
  const ufs = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const res = await api.get('/categories');
        if (res.data.success) {
          setAvailableCategories(res.data.data || []);
        }
      } catch (err) {
        console.error('Erro ao carregar catálogo de categorias:', err);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const categoryCatalogMap = useMemo(() => {
    return new Map(availableCategories.map((cat) => [cat.value, cat]));
  }, [availableCategories]);

  const getDomainCategories = (domain) => {
    return Array.isArray(domain?.categories) ? domain.categories.filter(Boolean) : [];
  };

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await getDomains({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm || undefined,
        category: filterCategory || undefined,
        categories: filterStrategicCategories.length > 0
          ? filterStrategicCategories.join(',')
          : undefined,
        tier: filterTier || undefined,
        sort: sortField,
        order: sortDirection
      });
      setDomains(response.data.data);
      setTotalDomains(response.data.pagination?.total || response.data.data.length);
      setTotalPages(response.data.pagination?.pages || 1);
      setError('');
    } catch (error) {
      console.error('Erro ao buscar domínios:', error);
      console.error('Detalhes do erro:', error.response?.data);
      setError('Erro ao carregar domínios: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Debounce do termo de busca para evitar requisições a cada tecla
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchDomains();
  }, [debouncedSearchTerm, filterCategory, filterStrategicCategories, filterTier, currentPage, pageSize, sortField, sortDirection]);

  // Funções para paginação
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset para primeira página
  };

  // Funções para ordenação
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset para primeira página ao ordenar
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />;
  };

  const handleEditClick = (domain) => {
    setSelectedDomain(domain);
    const ppv = domain.ppv || 2.5;
    // Inicializar texto do PPV com formato brasileiro (vírgula)
    setPpvText(ppv.toString().replace('.', ','));
      const domainCategories = Array.isArray(domain.categories)
        ? domain.categories.filter(Boolean)
        : [];
    setDomainData({
      domain: domain.domain || '',
      siteName: domain.siteName || '',
      category: domain.category || 'site',
      categories: domainCategories,
      estado: domain.estado || '',
      uf: domain.uf || '',
      tipo: domain.tipo || 'Site',
      audience: domain.audience || 0,
      audienceMonthly: domain.audienceMonthly || 0,
      audienceDaily: domain.audienceDaily || 0,
      subscribers: domain.subscribers || 0,
      pageViews: domain.pageViews || 0,
      pageViewsMonthly: domain.pageViewsMonthly || 0,
      ppv: ppv,
      valuation: domain.valuation || 0,
      tier: domain.tier || 3,
      description: domain.description || '',
      updateSource: domain.updateSource || 'automatic'
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      if (selectedDomain) {
        await updateDomain(selectedDomain._id, domainData);
      } else {
        await createDomain(domainData);
      }
      setEditDialogOpen(false);
      fetchDomains();
    } catch (error) {
      console.error('Erro ao salvar domínio:', error);
      setError('Erro ao salvar domínio: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteDomain = (domain) => {
    setDomainToDelete(domain);
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!domainToDelete) return;

    try {
      await deleteDomain(domainToDelete._id);
      setConfirmDeleteDialogOpen(false);
      setDomainToDelete(null);
      fetchDomains();
    } catch (error) {
      console.error('Erro ao excluir domínio:', error);
      setError('Erro ao excluir domínio');
      setConfirmDeleteDialogOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteDialogOpen(false);
    setDomainToDelete(null);
  };

  const handleViewHistory = async (domain) => {
    try {
      setHistoryLoading(true);
      setSelectedDomain(domain);
      setHistoryDialogOpen(true);

      // Usar axios configurado em vez de fetch
      const response = await api.get(`/domain-info/${domain._id}/history`);
      setDomainHistory(response.data.data || []);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      setError('Erro ao carregar histórico');
      setDomainHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'info';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'portal': return 'primary';
      case 'site': return 'success';
      case 'blog': return 'info';
      case 'rede-social': return 'secondary';
      case 'governo': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 4 }}>
        <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main', mb: 3 }} />
        <Typography variant="h6" color="text.primary" fontWeight={600} mb={1}>
          Carregando Base de Domínios
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sincronizando com os clusters de Valoração...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4} sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 3, 
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            color: 'primary.main', 
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DomainIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
              Gerenciar Domínios
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
              {totalDomains.toLocaleString('pt-BR')} domínios registrados na base
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedDomain(null);
            setPpvText('2,5'); // Reset PPV text
            setDomainData({
              domain: '', siteName: '', category: 'site', categories: [], estado: '', uf: '', tipo: 'Site',
              audience: 0, audienceMonthly: 0, audienceDaily: 0, subscribers: 0,
              pageViews: 0, pageViewsMonthly: 0, ppv: 2.5, valuation: 0, tier: 3, description: ''
            });
            setEditDialogOpen(true);
          }}
          sx={{ 
            borderRadius: '12px', 
            px: 3, 
            py: 1.2, 
            boxShadow: theme.palette.mode === 'dark' ? '0 4px 14px 0 rgba(255,255,255,0.1)' : '0 4px 14px 0 rgba(0,118,255,0.39)', 
            fontWeight: 600, 
            textTransform: 'none',
            letterSpacing: '0.2px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', 
            '&:hover': { 
              transform: 'translateY(-2px)', 
              boxShadow: theme.palette.mode === 'dark' ? '0 6px 20px rgba(255,255,255,0.15)' : '0 6px 20px rgba(0,118,255,0.23)' 
            } 
          }}
        >
          Adicionar Domínio
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filtros Modernos */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          background: theme.palette.background.paper,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
          borderRadius: 4,
          position: 'sticky',
          top: 24, // Ajustado para não esconder no header top
          zIndex: 10,
          boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.2)' : '0 8px 32px rgba(0, 0, 0, 0.04)'
        }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Buscar por domínio ou site..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
                  sx: { borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  sx={{ borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)' }}
                >
                  <MenuItem value="">Todas as Categorias</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={filterTier}
                  onChange={(e) => setFilterTier(e.target.value)}
                  sx={{ borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)' }}
                >
                  <MenuItem value="">Todos Tiers</MenuItem>
                  <MenuItem value="1">Tier 1</MenuItem>
                  <MenuItem value="2">Tier 2</MenuItem>
                  <MenuItem value="3">Tier 3</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  sx={{ borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)' }}
                >
                  <MenuItem value={25}>25 por pág.</MenuItem>
                  <MenuItem value={50}>50 por pág.</MenuItem>
                  <MenuItem value={100}>100 por pág.</MenuItem>
                  <MenuItem value={200}>200 por pág.</MenuItem>
                  </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={availableCategories}
                loading={loadingCategories}
                value={filterStrategicCategories
                  .map((value) => categoryCatalogMap.get(value) || { value, label: value })}
                onChange={(event, newValue) => {
                  setFilterStrategicCategories(
                    newValue.map((item) => item.value || item.label).filter(Boolean)
                  );
                  setCurrentPage(1);
                }}
                getOptionLabel={(option) => option?.label || option?.value || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filtrar por categorias estratégicas"
                    placeholder="Ex.: Moda, Negócios, Tecnologia"
                    helperText="Filtra pelas categorias reais usadas no match de Tier."
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)' } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value || option.label}
                      label={option.label || option.value}
                      size="small"
                      sx={{ borderRadius: '6px', fontWeight: 600 }}
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela de Domínios Premium */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.4)' : '#ffffff',
          borderRadius: 4,
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 24px rgba(0,0,0,0.2)' : '0 4px 24px rgba(0,0,0,0.02)',
          maxHeight: 'calc(100vh - 220px)',
          '&::-webkit-scrollbar': { width: '8px', height: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: theme.palette.divider }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Exibindo {domains.length} de {totalDomains.toLocaleString('pt-BR')} domínios na página atual
          </Typography>
        </Box>
        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid', borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fcfcfc', fontWeight: 600 }}>
                <TableSortLabel
                  active={sortField === 'domain'}
                  direction={sortField === 'domain' ? sortDirection : 'asc'}
                  onClick={() => handleSort('domain')}
                >
                  Domínio
                  {getSortIcon('domain')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fcfcfc', fontWeight: 600 }}>
                <TableSortLabel
                  active={sortField === 'siteName'}
                  direction={sortField === 'siteName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('siteName')}
                >
                  Nome do Site
                  {getSortIcon('siteName')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'category'}
                  direction={sortField === 'category' ? sortDirection : 'asc'}
                  onClick={() => handleSort('category')}
                >
                  Categoria
                  {getSortIcon('category')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'estado'}
                  direction={sortField === 'estado' ? sortDirection : 'asc'}
                  onClick={() => handleSort('estado')}
                >
                  Estado
                  {getSortIcon('estado')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'tipo'}
                  direction={sortField === 'tipo' ? sortDirection : 'asc'}
                  onClick={() => handleSort('tipo')}
                >
                  Tipo
                  {getSortIcon('tipo')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'tier'}
                  direction={sortField === 'tier' ? sortDirection : 'asc'}
                  onClick={() => handleSort('tier')}
                >
                  Tier
                  {getSortIcon('tier')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'audience'}
                  direction={sortField === 'audience' ? sortDirection : 'asc'}
                  onClick={() => handleSort('audience')}
                >
                  Audiência
                  {getSortIcon('audience')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'valuation'}
                  direction={sortField === 'valuation' ? sortDirection : 'asc'}
                  onClick={() => handleSort('valuation')}
                >
                  Valoração
                  {getSortIcon('valuation')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'updateSource'}
                  direction={sortField === 'updateSource' ? sortDirection : 'asc'}
                  onClick={() => handleSort('updateSource')}
                >
                  Modo
                  {getSortIcon('updateSource')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'lastUpdated'}
                  direction={sortField === 'lastUpdated' ? sortDirection : 'asc'}
                  onClick={() => handleSort('lastUpdated')}
                >
                  Última Atualização
                  {getSortIcon('lastUpdated')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fcfcfc', fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains.map((domain) => (
              <TableRow 
                key={domain._id} 
                hover
                sx={{ 
                  transition: 'background-color 0.2s ease',
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03) !important' : 'rgba(0,0,0,0.02) !important' }
                }}
              >
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: 'primary.dark',
                      }
                    }}
                    onClick={() => {
                      const url = domain.domain.startsWith('http') ? domain.domain : `https://${domain.domain}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    {domain.domain}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500} color="text.primary">
                    {domain.siteName || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {getDomainCategories(domain).length > 0 ? (
                    getDomainCategories(domain).map((categoryValue) => {
                      const categoryMeta = categoryCatalogMap.get(categoryValue);
                      const label = categoryMeta?.label || categoryValue;
                      return (
                        <Chip
                          key={categoryValue}
                          label={label}
                          size="small"
                          color={getCategoryColor(categoryValue)}
                          sx={{ borderRadius: '6px', fontWeight: 600, px: 0.5, letterSpacing: '0.3px' }}
                        />
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{domain.estado || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{domain.tipo || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={domain.tier === 1 ? 'Tier 1' : domain.tier === 2 ? 'Tier 2' : 'Tier 3'}
                    size="small"
                    color={getTierColor(domain.tier)}
                    variant="outlined"
                    sx={{ borderRadius: '6px', fontWeight: 700, borderWidth: '1.5px' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {domain.audience ? domain.audience.toLocaleString('pt-BR') : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {domain.valuation ? `R$ ${domain.valuation.toLocaleString('pt-BR')}` : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={domain.updateSource === 'manual' ? 'Manual' : 'Auto'}
                    size="small"
                    color={domain.updateSource === 'manual' ? 'warning' : 'default'}
                    variant={domain.updateSource === 'manual' ? 'filled' : 'outlined'}
                    sx={{ minWidth: 60, borderRadius: '6px', fontWeight: 600, borderColor: theme.palette.divider }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {domain.lastUpdated
                      ? new Date(domain.lastUpdated).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                      : new Date(domain.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    }
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(domain)}
                      sx={{ color: 'primary.main', bgcolor: 'transparent', '&:hover': { bgcolor: 'primary.soft' } }}
                      title="Editar domínio"
                    >
                      <EditIcon sx={{ fontSize: '1.35rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewHistory(domain)}
                      sx={{ color: 'info.main', bgcolor: 'transparent', '&:hover': { bgcolor: 'info.soft' } }}
                      title="Ver histórico de edições"
                    >
                      <HistoryIcon sx={{ fontSize: '1.35rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteDomain(domain)}
                      sx={{ color: 'error.main', bgcolor: 'transparent', '&:hover': { bgcolor: 'error.soft' } }}
                      title="Excluir domínio"
                    >
                      <DeleteIcon sx={{ fontSize: '1.35rem' }} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Confirmação de Exclusão */}
      <ConfirmDialog
        open={confirmDeleteDialogOpen}
        title="Excluir Domínio"
        message={`Tem certeza que deseja excluir o domínio ${domainToDelete?.domain}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />


      {/* Paginação */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Stack spacing={2} alignItems="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
            <Typography variant="body2" color="text.secondary">
              Página {currentPage} de {totalPages} • {totalDomains.toLocaleString('pt-BR')} domínios total
            </Typography>
          </Stack>
        </Box>
      )
      }

      {/* Dialog de Edição */}
      <PlatformDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <PlatformDialogTitle 
          title={selectedDomain ? 'Editar Configuração de Domínio' : 'Cadastrar Novo Domínio'}
          subtitle={selectedDomain ? `ID: ${selectedDomain._id}` : 'Insira os dados técnicos da fonte Web.'}
          icon={<DomainIcon />}
          onClose={() => setEditDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {domainData.updateSource === 'manual' && (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ borderRadius: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      const newData = { ...domainData, updateSource: 'automatic' };
                      setDomainData(newData);
                    }}
                    sx={{ fontWeight: 700 }}
                  >
                    REVERTER PARA AUTO
                  </Button>
                }
              >
                <strong>Modo Manual Ativo:</strong> Este domínio não receberá atualizações automáticas via SimilarWeb.
              </Alert>
            )}
            
            {domainData.updateSource === 'automatic' && selectedDomain && (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                <strong>Atualização Automática:</strong> Este domínio é sincronizado periodicamente. Alterar campos de audiência ativará o modo manual.
              </Alert>
            )}

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Domínio (URL Principal)"
                  size="small"
                  value={domainData.domain}
                  onChange={(e) => setDomainData({ ...domainData, domain: e.target.value })}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome Amigável do Site"
                  size="small"
                  value={domainData.siteName}
                  onChange={(e) => setDomainData({ ...domainData, siteName: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Categorias estruturais</InputLabel>
                  <Select
                    value={domainData.category}
                    label="Categorias estruturais"
                    onChange={(e) => setDomainData({ ...domainData, category: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    {categories.map(cat => (
                      <MenuItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Estado / Localidade"
                  size="small"
                  value={domainData.estado}
                  onChange={(e) => setDomainData({ ...domainData, estado: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Veículo</InputLabel>
                  <Select
                    value={domainData.tipo}
                    label="Tipo de Veículo"
                    onChange={(e) => setDomainData({ ...domainData, tipo: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    {tipos.map(tipo => (
                      <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                  <Autocomplete
                  multiple
                  options={availableCategories}
                  loading={loadingCategories}
                      value={getDomainCategories(domainData)
                        .map((value) => categoryCatalogMap.get(value) || { value, label: value })}
                  onChange={(event, newValue) => {
                    const categoriesValues = newValue.map((item) => item.value || item.label).filter(Boolean);
                    setDomainData({ ...domainData, categories: categoriesValues });
                  }}
                  getOptionLabel={(option) => option?.label || option?.value || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categorias estratégicas"
                      placeholder="Selecione as categorias que alimentam o match de Tier"
                      helperText="Essas categorias são usadas para comparar a fonte com os segmentos configurados na marca."
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value || option.label}
                        label={option.label || option.value}
                        size="small"
                        sx={{ borderRadius: '6px', fontWeight: 600 }}
                      />
                    ))
                  }
                  sx={{ '& .MuiAutocomplete-inputRoot': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Métricas de Valoração" size="small" sx={{ fontWeight: 700 }} />
                </Divider>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Audiência Mensal"
                  size="small"
                  type="text"
                  value={formatNumber(domainData.audienceMonthly)}
                  onChange={(e) => {
                    const rawVal = e.target.value;
                    if (!/^[0-9.,]*$/.test(rawVal)) return;
                    const numVal = parseNumber(rawVal);
                    const derived = recalculateValues(numVal, domainData.ppv);
                    setDomainData({
                      ...domainData,
                      audienceMonthly: numVal,
                      ...derived,
                      updateSource: 'manual'
                    });
                  }}
                  InputProps={{
                    endAdornment: <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Visitantes</Typography>,
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="PPV (Páginas/Visita)"
                  size="small"
                  type="text"
                  value={ppvText}
                  onChange={(e) => {
                    const rawVal = e.target.value;
                    if (!/^[0-9]*[,.]?[0-9]*$/.test(rawVal)) return;
                    setPpvText(rawVal);
                  }}
                  onBlur={() => {
                    const numVal = parseNumber(ppvText, true) || 2.5;
                    const derived = recalculateValues(domainData.audienceMonthly, numVal);
                    setPpvText(numVal.toString().replace('.', ','));
                    setDomainData({
                      ...domainData,
                      ppv: numVal,
                      ...derived,
                      updateSource: 'manual'
                    });
                  }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Seguidores / Inscritos"
                  size="small"
                  type="text"
                  value={formatNumber(domainData.subscribers)}
                  onChange={(e) => {
                    const val = parseNumber(e.target.value);
                    setDomainData({
                      ...domainData,
                      subscribers: val,
                      updateSource: 'manual'
                    });
                  }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Audiência Diária"
                  size="small"
                  value={formatNumber(domainData.audienceDaily)}
                  InputProps={{ readOnly: true, sx: { borderRadius: 2, bgcolor: 'action.hover' } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="PV Mensal"
                  size="small"
                  value={formatNumber(domainData.pageViewsMonthly)}
                  InputProps={{ readOnly: true, sx: { borderRadius: 2, bgcolor: 'action.hover' } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="PV Diário"
                  size="small"
                  value={formatNumber(domainData.pageViews)}
                  InputProps={{ readOnly: true, sx: { borderRadius: 2, bgcolor: 'action.hover' } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Valoração"
                  size="small"
                  value={`R$ ${formatNumber(domainData.valuation)}`}
                  InputProps={{ 
                    readOnly: true, 
                    sx: { borderRadius: 2, bgcolor: 'primary.light', color: 'primary.contrastText', fontWeight: 700 } 
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Enquadramento Tier</InputLabel>
                  <Select
                    value={domainData.tier}
                    label="Enquadramento Tier"
                    onChange={(e) => setDomainData({ ...domainData, tier: parseInt(e.target.value) })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value={1}>Tier 1 (Nível Nacional / Mega Portais)</MenuItem>
                    <MenuItem value={2}>Tier 2 (Nível Estadual / Grande Influência)</MenuItem>
                    <MenuItem value={3}>Tier 3 (Nível Local / Blogs / Nicho)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações Internas"
                  multiline
                  rows={3}
                  size="small"
                  value={domainData.description}
                  onChange={(e) => setDomainData({ ...domainData, description: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button onClick={handleEditSubmit} variant="contained" sx={{ fontWeight: 700, borderRadius: 2, px: 4 }}>
            Salvar Configurações
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog de Histórico */}
      <PlatformDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <PlatformDialogTitle 
          title="Registro de Alterações"
          subtitle={`Histórico técnico de ${selectedDomain?.domain}`}
          icon={<HistoryIcon />}
          onClose={() => setHistoryDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {historyLoading ? (
              <Box display="flex" flexDirection="column" gap={1} p={4}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              </Box>
            ) : domainHistory.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <HistoryIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.disabled">
                  Nenhuma alteração registrada neste domínio.
                </Typography>
              </Box>
            ) : (
              <List sx={{ pt: 0 }}>
                {domainHistory.map((log, index) => (
                  <React.Fragment key={log._id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        px: 0, 
                        py: 2,
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 2
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                            <Chip
                              label={log.action === 'created' ? 'CRIAÇÃO' : log.action === 'updated' ? 'EDIÇÃO' : 'EXCLUSÃO'}
                              size="small"
                              color={log.action === 'created' ? 'success' : log.action === 'updated' ? 'primary' : 'error'}
                              sx={{ fontWeight: 800, fontSize: '0.65rem' }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {new Date(log.createdAt).toLocaleString('pt-BR')}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                              OPERADOR: {log.editedBy?.name || 'Sistema Automatico'} • IP: {log.ipAddress}
                            </Typography>
                            {Object.keys(log.changes || {}).length > 0 && (
                              <Box sx={{ 
                                bgcolor: 'background.paper', 
                                border: '1px solid', 
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                p: 1.5,
                                mt: 1
                              }}>
                                <Grid container spacing={1}>
                                  {Object.keys(log.changes).map(field => (
                                    <Grid item xs={12} sm={6} key={field}>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', minWidth: 60 }}>
                                          {field}:
                                        </Typography>
                                        <Typography variant="caption" sx={{ flex: 1, noWrap: true }}>
                                          {log.changes[field].from ?? '-'} → <Box component="span" sx={{ fontWeight: 700 }}>{log.changes[field].to ?? '-'}</Box>
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < domainHistory.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setHistoryDialogOpen(false)} variant="outlined" fullWidth sx={{ fontWeight: 600, borderRadius: 2 }}>
            Fechar Histórico
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box >
  );
};

export default DomainsManagementPanel;
